// public/js/base/baseMessages.js

import { createLogger } from '@utils/logger.js';

/**
 * BaseMessages - Centralized toast notification system
 * FIXED: Prevents duplicate messages and works with external CSS
 */
class BaseMessages {
    constructor() {
        this.logger = createLogger('BaseMessages');
        this.container = null;
        this.messageQueue = [];
        this.activeMessages = new Map();
        this.messageIdCounter = 0;
        
        // ADDED: Duplicate prevention
        this.recentMessages = new Map(); // message content -> timestamp
        this.duplicateThreshold = 1000; // 1 second
        
        // Configuration
        this.config = {
            maxMessages: 5,
            defaultDuration: 4000,
            durations: {
                success: 3000,
                error: 6000,
                warning: 5000,
                info: 4000
            },
            position: 'top-right',
            animationDuration: 300 // Still needed for JS animations
        };
        
        this.init();
        this.logger.info('BaseMessages initialized with duplicate prevention');
    }

    /**
     * Initialize the messages container
     */
    init() {
        // Create container if it doesn't exist
        if (!this.container) {
            this.createContainer();
        }
        
        // Clean up any existing messages
        this.clearAll();
    }

    /**
     * Create the messages container - NO CSS INJECTION
     */
    createContainer() {
        // Remove existing container if present
        const existing = document.getElementById('base-messages-container');
        if (existing) {
            existing.remove();
        }
        
        this.container = document.createElement('div');
        this.container.id = 'base-messages-container';
        this.container.className = `base-messages-container ${this.config.position}`;
        
        // Set CSS custom property for animation duration
        this.container.style.setProperty('--animation-duration', `${this.config.animationDuration}ms`);
        
        // Append to body
        document.body.appendChild(this.container);
        
        this.logger.debug('Messages container created without CSS injection');
    }

    /**
     * REMOVED: CSS injection - now relies on external CSS file
     * Your external CSS should include all the base-message styles
     */

    /**
     * Show a success message
     * @param {string} message - Message text
     * @param {Object} options - Additional options
     */
    success(message, options = {}) {
        return this.show(message, 'success', options);
    }

    /**
     * Show an error message
     * @param {string} message - Message text
     * @param {Object} options - Additional options
     */
    error(message, options = {}) {
        return this.show(message, 'error', options);
    }

    /**
     * Show a warning message
     * @param {string} message - Message text
     * @param {Object} options - Additional options
     */
    warning(message, options = {}) {
        return this.show(message, 'warning', options);
    }

    /**
     * Show an info message
     * @param {string} message - Message text
     * @param {Object} options - Additional options
     */
    info(message, options = {}) {
        return this.show(message, 'info', options);
    }

    /**
     * Show a message - FIXED: Prevents duplicates
     * @param {string} message - Message text
     * @param {string} type - Message type (success, error, warning, info)
     * @param {Object} options - Additional options
     * @returns {string} Message ID or null if duplicate
     */
    show(message, type = 'info', options = {}) {
        if (!message || typeof message !== 'string') {
            this.logger.warn('Invalid message provided:', message);
            return null;
        }
        
        // ADDED: Check for recent duplicates
        const messageKey = `${type}:${message}`;
        const now = Date.now();
        const lastShown = this.recentMessages.get(messageKey);
        
        if (lastShown && (now - lastShown) < this.duplicateThreshold) {
            this.logger.debug(`Duplicate message blocked: ${messageKey}`);
            return null; // Don't show duplicate
        }
        
        // Record this message
        this.recentMessages.set(messageKey, now);
        
        // Clean up old entries from recent messages
        this.cleanupRecentMessages();
        
        // Ensure container exists
        if (!this.container || !document.body.contains(this.container)) {
            this.createContainer();
        }
        
        const messageId = `msg_${++this.messageIdCounter}`;
        const duration = options.duration !== undefined ? options.duration : this.config.durations[type];
        const persistent = options.persistent === true;
        
        const messageData = {
            id: messageId,
            message: message,
            type: type,
            title: options.title || this.getDefaultTitle(type),
            duration: duration,
            persistent: persistent,
            timestamp: Date.now()
        };
        
        // Remove oldest message if we have too many
        if (this.activeMessages.size >= this.config.maxMessages) {
            this.removeOldestMessage();
        }
        
        // Create and show message element
        const messageElement = this.createMessageElement(messageData);
        this.container.appendChild(messageElement);
        
        // Store message data
        this.activeMessages.set(messageId, {
            ...messageData,
            element: messageElement,
            timeoutId: null
        });
        
        // Trigger animation
        setTimeout(() => {
            messageElement.classList.add('show');
        }, 10);
        
        // Auto-dismiss if not persistent
        if (!persistent && duration > 0) {
            this.scheduleRemoval(messageId, duration);
        }
        
        this.logger.debug(`Message shown: ${type} - ${message}`);
        return messageId;
    }

    /**
     * Create message DOM element - FIXED: Works with external CSS
     * @param {Object} messageData - Message data
     * @returns {HTMLElement} Message element
     */
    createMessageElement(messageData) {
        const element = document.createElement('div');
        element.className = `base-message ${messageData.type}`;
        element.dataset.messageId = messageData.id;
        
        const icons = {
            success: 'check_circle',
            error: 'error',
            warning: 'warning',
            info: 'info'
        };
        
        element.innerHTML = `
            <div class="base-message-header">
                <span class="base-message-icon">${icons[messageData.type] || 'info'}</span>
                <span class="base-message-title">${messageData.title}</span>
                <button class="base-message-close" onclick="baseMessages.dismiss('${messageData.id}')">close</button>
            </div>
            <div class="base-message-body">${messageData.message}</div>
            ${!messageData.persistent && messageData.duration > 0 ? '<div class="base-message-progress"></div>' : ''}
        `;
        
        // FIXED: Set progress bar animation duration via JavaScript
        if (!messageData.persistent && messageData.duration > 0) {
            const progressBar = element.querySelector('.base-message-progress');
            if (progressBar) {
                // Set CSS custom property for this specific progress bar
                progressBar.style.setProperty('--progress-duration', `${messageData.duration}ms`);
                
                setTimeout(() => {
                    progressBar.style.width = '100%';
                    progressBar.style.transition = `width var(--progress-duration, ${messageData.duration}ms) linear`;
                }, 100);
            }
        }
        
        return element;
    }

    /**
     * Clean up recent messages cache
     */
    cleanupRecentMessages() {
        const now = Date.now();
        const expireTime = this.duplicateThreshold * 2; // Keep for twice the threshold
        
        for (const [key, timestamp] of this.recentMessages.entries()) {
            if (now - timestamp > expireTime) {
                this.recentMessages.delete(key);
            }
        }
    }

    /**
     * Schedule message removal
     * @param {string} messageId - Message ID
     * @param {number} duration - Duration in milliseconds
     */
    scheduleRemoval(messageId, duration) {
        const messageData = this.activeMessages.get(messageId);
        if (!messageData) return;
        
        if (messageData.timeoutId) {
            clearTimeout(messageData.timeoutId);
        }
        
        messageData.timeoutId = setTimeout(() => {
            this.dismiss(messageId);
        }, duration);
        
        this.activeMessages.set(messageId, messageData);
    }

    /**
     * Dismiss a specific message
     * @param {string} messageId - Message ID
     */
    dismiss(messageId) {
        const messageData = this.activeMessages.get(messageId);
        if (!messageData) return;
        
        // Clear timeout
        if (messageData.timeoutId) {
            clearTimeout(messageData.timeoutId);
        }
        
        // Animate out
        messageData.element.classList.remove('show');
        
        // Remove from DOM after animation
        setTimeout(() => {
            if (messageData.element.parentNode) {
                messageData.element.parentNode.removeChild(messageData.element);
            }
            this.activeMessages.delete(messageId);
        }, this.config.animationDuration);
        
        this.logger.debug(`Message dismissed: ${messageId}`);
    }

    /**
     * Remove the oldest message
     */
    removeOldestMessage() {
        let oldestId = null;
        let oldestTime = Date.now();
        
        for (const [id, data] of this.activeMessages.entries()) {
            if (data.timestamp < oldestTime) {
                oldestTime = data.timestamp;
                oldestId = id;
            }
        }
        
        if (oldestId) {
            this.dismiss(oldestId);
        }
    }

    /**
     * Clear all messages
     */
    clearAll() {
        for (const messageId of this.activeMessages.keys()) {
            this.dismiss(messageId);
        }
    }

    /**
     * Get default title for message type
     * @param {string} type - Message type
     * @returns {string} Default title
     */
    getDefaultTitle(type) {
        const titles = {
            success: 'Success',
            error: 'Error',
            warning: 'Warning',
            info: 'Information'
        };
        return titles[type] || 'Message';
    }

    /**
     * Update configuration
     * @param {Object} newConfig - New configuration options
     */
    updateConfig(newConfig) {
        Object.assign(this.config, newConfig);
        
        // Recreate container if position changed
        if (newConfig.position && this.container) {
            this.container.className = `base-messages-container ${this.config.position}`;
        }
        
        // Update animation duration CSS property
        if (newConfig.animationDuration && this.container) {
            this.container.style.setProperty('--animation-duration', `${this.config.animationDuration}ms`);
        }
        
        this.logger.debug('Configuration updated:', newConfig);
    }

    /**
     * Get current statistics
     * @returns {Object} Statistics
     */
    getStats() {
        return {
            activeMessages: this.activeMessages.size,
            maxMessages: this.config.maxMessages,
            queueLength: this.messageQueue.length,
            recentMessagesCount: this.recentMessages.size
        };
    }

    /**
     * ADDED: Manual cleanup method
     */
    cleanup() {
        this.clearAll();
        this.recentMessages.clear();
        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
            this.container = null;
        }
    }
}

// Create and export singleton instance
const baseMessages = new BaseMessages();

// Make available globally for onclick handlers
if (typeof window !== 'undefined') {
    window.baseMessages = baseMessages;
}

export default baseMessages;